import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PROFILE_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PROFILE_SERVICE_BASE_URL}/api/v1/feedback`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 50;

export default function useFeedbacks() {
  const authToken = sessionStorage.getItem("authToken");
  const [feedbacks, setFeedbacks] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      const status = err.response.status;

      if (status >= 500 && status < 600) {
        message = "Something went wrong. Please try again later.";
      } else if (status === 400 || status === 401) {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch feedbacks
  const fetchAllFeedbacks = useCallback(
    async (search = "", page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        const trimmedSearch = String(search || "").trim();
        if (trimmedSearch) {
          params.append("search", trimmedSearch);
        }

        const res = await apiClient.get(`${BASE_URL}?${params.toString()}`);

        let feedbackList = [];
        if (Array.isArray(res.data)) {
          feedbackList = res.data;
        } else if (Array.isArray(res.data?.content)) {
          feedbackList = res.data.content;
        } else if (Array.isArray(res.data?.data)) {
          feedbackList = res.data.data;
        }

        setFeedbacks(feedbackList.map(formatFeedbackData));
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || feedbackList.length || 0);
      } catch (error) {
        handleAxiosErrors(error, "fetch all feedbacks");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  /** -------------------- Fetch Feedbacks -------------------- **/

const searchFeedbacks = useCallback(
  async (
    query = "",
    status = "ALL",
    feeling = "ALL",
    subject = "",
    searchPage = DEFAULT_PAGE,
    size = DEFAULT_SIZE,
  ) => {
  if (!authToken) return;
  setLoading(true);
  setError(null);

  try {
    const trimmedQuery = query?.trim() || "";
    
    const params = new URLSearchParams();

    params.append("page", String(searchPage));
    params.append("size", String(size));
    
    // 1. Filter by status (e.g., 'PENDING', 'RESOLVED', 'ARCHIVED')
    if (status && status !== "ALL") {
      params.append("status", status);
    }

    // 2. Filter by feeling/subject (exact match filters)
    if (feeling && feeling !== "ALL") {
      params.append("feeling", feeling);
    }

    const trimmedSubject = String(subject || "").trim();
    if (trimmedSubject) {
      params.append("subject", trimmedSubject);
    }

    // 3. Add query for text-based search (3+ characters)
    if (trimmedQuery.length >= 3) {
      params.append("q", trimmedQuery);
    }

    // 4. Determine endpoint: use /search if filters are applied
    const isFiltered =
      params.has("status") ||
      params.has("feeling") ||
      params.has("subject") ||
      params.has("q");
    const endpoint = isFiltered ? `${BASE_URL}/search` : BASE_URL;
    
    const queryString = params.toString();
    const finalUrl = queryString ? `${endpoint}?${queryString}` : endpoint;

    const response = await apiClient.get(finalUrl);
    
    // Destructure based on your API's pagination structure
    const { content = [], totalPages = 1, totalElements = 0 } = response.data;

    // Update state (Ensure you have these setters defined in your component)
    setFeedbacks(content.map(formatFeedbackData)); 
    setTotalPages(totalPages);
    setTotalElements(totalElements);

  } catch (err) {
    setError(err.response?.data?.message || err.message);
  } finally {
    setLoading(false);
  }
}, [authToken]);

  // fetch feedback by id
  const fetchFeedbackById = useCallback(
    async (feedbackId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.get(`${BASE_URL}/${feedbackId}`);
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch feedback by id");
      }
    },
    [authToken],
  );

  // delete feedback
  const deleteFeedbackById = useCallback(
    async (feedbackId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_URL}/${feedbackId}`);

        setFeedbacks((prev) =>
          prev.filter((feedback) => feedback.id !== feedbackId),
        );
        toast.success("Feedback deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete feedback");
      }
    },
    [authToken],
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchAllFeedbacks();
  }, [authToken, fetchAllFeedbacks]);

  return {
    feedbacks,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAllFeedbacks,
    fetchFeedbackById,
    deleteFeedbackById,
    searchFeedbacks,
  };
}

const formatFeedbackData = (feedback = {}) => {
  return {

    id: feedback.id || null,
    userId: feedback.userId || null,
    subjectId: feedback.subjectId || null,
    content: feedback.content || "",
    subject: feedback.subject || "No Subject",
    feeling: feedback.feeling || "NEUTRAL",

    status: feedback.status || "PENDING",
    createdAt: feedback.createdAt || null,

    ...feedback 
  }
};
